import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'

// ─── Constants ────────────────────────────────────────────────────────────────

const STRUCTURE_TYPES = [
    { value: 'SWAP', label: 'Swap' },
    { value: 'PUT', label: 'Put' },
    { value: 'COLLAR', label: 'Collar' },
    { value: 'THREE_WAY_COLLAR', label: 'Three-Way Collar' },
    { value: 'PARTICIPATING_COLLAR', label: 'Participating Collar' },
    { value: 'SWAPTION', label: 'Swaption' },
]

const TYPE_COLORS = {
    SWAP: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    PUT: 'bg-green-500/20 text-green-400 border-green-500/30',
    COLLAR: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    THREE_WAY_COLLAR: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    PARTICIPATING_COLLAR: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    SWAPTION: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
}

const TYPE_COLORS_LIGHT = {
    SWAP: 'bg-blue-100 text-blue-700 border-blue-200',
    PUT: 'bg-green-100 text-green-700 border-green-200',
    COLLAR: 'bg-purple-100 text-purple-700 border-purple-200',
    THREE_WAY_COLLAR: 'bg-orange-100 text-orange-700 border-orange-200',
    PARTICIPATING_COLLAR: 'bg-teal-100 text-teal-700 border-teal-200',
    SWAPTION: 'bg-pink-100 text-pink-700 border-pink-200',
}

const EMPTY_FORM = {
    structureType: 'SWAP',
    contractMonth: '',
    volumeBBL: '',
    swapPrice: '',
    putStrike: '',
    callStrike: '',
    shortPutStrike: '',
    participationRate: '',
    premiumPaid: '',
    swaptionStrike: '',
    swaptionExpiry: '',
    swaptionPremium: '',
    counterparty: '',
    notes: '',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(val) {
    if (val == null || val === '') return '--'
    return `$${Number(val).toFixed(2)}`
}

function fmtMTM(val) {
    if (val == null || isNaN(val)) return '--'
    const abs = Math.abs(val)
    const prefix = val < 0 ? '-$' : '$'
    if (abs >= 1_000_000) return `${prefix}${(abs / 1_000_000).toFixed(2)}M`
    if (abs >= 1_000) return `${prefix}${(abs / 1_000).toFixed(1)}K`
    return `${prefix}${Math.round(abs).toLocaleString()}`
}

function calcMTM(pos) {
    const strip = Number(pos.StripPrice) || 0
    const vol = Number(pos.VolumeBBL) || 0
    const premium = Number(pos.PremiumPaid) || 0

    switch (pos.StructureType) {
        case 'SWAP':
            return (Number(pos.SwapPrice) - strip) * vol

        case 'PUT':
            return (Math.max(0, Number(pos.PutStrike) - strip) - premium) * vol

        case 'COLLAR': {
            const put = Number(pos.PutStrike) || 0
            const call = Number(pos.CallStrike) || 0
            return (Math.max(0, put - strip) - Math.max(0, strip - call) - premium) * vol
        }

        case 'THREE_WAY_COLLAR': {
            const put = Number(pos.PutStrike) || 0
            const call = Number(pos.CallStrike) || 0
            const shortPut = Number(pos.ShortPutStrike) || 0
            // Long put floor = max(0, put - strip), short put = -max(0, shortPut - strip), short call = -max(0, strip - call)
            const longPut = Math.max(0, put - strip)
            const shortPutVal = -Math.max(0, shortPut - strip)
            const shortCall = -Math.max(0, strip - call)
            return (longPut + shortPutVal + shortCall - premium) * vol
        }

        case 'PARTICIPATING_COLLAR': {
            const put = Number(pos.PutStrike) || 0
            const call = Number(pos.CallStrike) || 0
            const rate = Number(pos.ParticipationRate) || 0
            // Participate in upside above call at rate%
            const floorVal = Math.max(0, put - strip)
            const upsideVal = strip > call ? (strip - call) * (rate / 100) : 0
            return (floorVal + upsideVal - premium) * vol
        }

        case 'SWAPTION':
            // Swaption MTM depends on whether it has been exercised; show premium cost until exercised
            return -premium * vol

        default:
            return 0
    }
}

function getStrikeDisplay(pos) {
    switch (pos.StructureType) {
        case 'SWAP':
            return pos.SwapPrice ? `Swap ${fmtPrice(pos.SwapPrice)}` : '--'

        case 'PUT':
            return pos.PutStrike ? `Put ${fmtPrice(pos.PutStrike)}` : '--'

        case 'COLLAR':
            if (pos.PutStrike && pos.CallStrike) {
                return `Put ${fmtPrice(pos.PutStrike)} / Call ${fmtPrice(pos.CallStrike)}`
            }
            return '--'

        case 'THREE_WAY_COLLAR':
            if (pos.ShortPutStrike && pos.PutStrike && pos.CallStrike) {
                return `${fmtPrice(pos.ShortPutStrike)} / ${fmtPrice(pos.PutStrike)} / ${fmtPrice(pos.CallStrike)}`
            }
            return '--'

        case 'PARTICIPATING_COLLAR':
            if (pos.PutStrike && pos.CallStrike) {
                const rate = pos.ParticipationRate ? ` (${pos.ParticipationRate}%)` : ''
                return `Put ${fmtPrice(pos.PutStrike)} / Call ${fmtPrice(pos.CallStrike)}${rate}`
            }
            return '--'

        case 'SWAPTION':
            return pos.SwaptionStrike ? `Strike ${fmtPrice(pos.SwaptionStrike)}` : '--'

        default:
            return '--'
    }
}

function getTypeLabel(type) {
    return STRUCTURE_TYPES.find(t => t.value === type)?.label || type
}

function formatContractMonth(dateStr) {
    if (!dateStr) return '--'
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

// Clamp to first of month for the date input
function toFirstOfMonth(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr + 'T00:00:00')
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}-01`
}

// Build array of YYYY-MM-01 strings between startMonth and endMonth inclusive
function buildMonthRange(startMonth, endMonth) {
    if (!startMonth || !endMonth) return []
    const start = new Date(startMonth + 'T00:00:00')
    const end = new Date(endMonth + 'T00:00:00')
    if (start > end) return []
    const months = []
    const cur = new Date(start)
    while (cur <= end) {
        const y = cur.getFullYear()
        const m = String(cur.getMonth() + 1).padStart(2, '0')
        months.push(`${y}-${m}-01`)
        cur.setMonth(cur.getMonth() + 1)
    }
    return months
}

// ─── Field config by structure type ──────────────────────────────────────────

function getRequiredFields(type) {
    switch (type) {
        case 'SWAP': return ['swapPrice']
        case 'PUT': return ['putStrike', 'premiumPaid']
        case 'COLLAR': return ['putStrike', 'callStrike']
        case 'THREE_WAY_COLLAR': return ['putStrike', 'callStrike', 'shortPutStrike']
        case 'PARTICIPATING_COLLAR': return ['putStrike', 'callStrike', 'participationRate']
        case 'SWAPTION': return ['swaptionStrike', 'swaptionExpiry', 'swaptionPremium']
        default: return []
    }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypeBadge({ type, isDark }) {
    const colorClass = isDark ? (TYPE_COLORS[type] || 'bg-gray-700 text-gray-300') : (TYPE_COLORS_LIGHT[type] || 'bg-gray-100 text-gray-700')
    return (
        <span className={`inline-flex px-2 py-0.5 rounded border text-xs font-semibold ${colorClass}`}>
            {getTypeLabel(type)}
        </span>
    )
}

function MTMCell({ mtm, isDark }) {
    if (mtm == null || isNaN(mtm)) return <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>--</span>
    const isPositive = mtm >= 0
    const colorClass = isPositive
        ? (isDark ? 'text-green-400' : 'text-green-600')
        : (isDark ? 'text-red-400' : 'text-red-600')
    return <span className={`font-mono ${colorClass}`}>{fmtMTM(mtm)}</span>
}

// ─── Position Form Modal ──────────────────────────────────────────────────────

function PositionModal({ isDark, editPosition, onClose, onSaved }) {
    const isEdit = !!editPosition

    const [form, setForm] = useState(() => {
        if (isEdit) {
            return {
                structureType: editPosition.StructureType || 'SWAP',
                contractMonth: editPosition.ContractMonth
                    ? editPosition.ContractMonth.substring(0, 10)
                    : '',
                volumeBBL: editPosition.VolumeBBL ?? '',
                swapPrice: editPosition.SwapPrice ?? '',
                putStrike: editPosition.PutStrike ?? '',
                callStrike: editPosition.CallStrike ?? '',
                shortPutStrike: editPosition.ShortPutStrike ?? '',
                participationRate: editPosition.ParticipationRate ?? '',
                premiumPaid: editPosition.PremiumPaid ?? '',
                swaptionStrike: editPosition.SwaptionStrike ?? '',
                swaptionExpiry: editPosition.SwaptionExpiry
                    ? editPosition.SwaptionExpiry.substring(0, 10)
                    : '',
                swaptionPremium: editPosition.SwaptionPremium ?? '',
                counterparty: editPosition.Counterparty ?? '',
                notes: editPosition.Notes ?? '',
            }
        }
        return { ...EMPTY_FORM }
    })

    const [bulkMode, setBulkMode] = useState(!isEdit)
    const [bulkStart, setBulkStart] = useState('')
    const [bulkEnd, setBulkEnd] = useState('')
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState({})
    const [submitError, setSubmitError] = useState(null)

    const type = form.structureType
    const required = getRequiredFields(type)

    function setField(key, val) {
        setForm(prev => ({ ...prev, [key]: val }))
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: false }))
    }

    function validate() {
        const errs = {}
        if (!form.contractMonth && !bulkMode) errs.contractMonth = true
        if (bulkMode && !bulkStart) errs.bulkStart = true
        if (bulkMode && !bulkEnd) errs.bulkEnd = true
        if (!form.volumeBBL || isNaN(Number(form.volumeBBL)) || Number(form.volumeBBL) <= 0) errs.volumeBBL = true

        required.forEach(field => {
            if (form[field] === '' || form[field] == null) {
                errs[field] = true
            } else if (['swapPrice', 'putStrike', 'callStrike', 'shortPutStrike', 'participationRate', 'premiumPaid', 'swaptionStrike', 'swaptionPremium'].includes(field)) {
                if (isNaN(Number(form[field]))) errs[field] = true
            }
        })

        return errs
    }

    async function handleSubmit(e) {
        e.preventDefault()
        const errs = validate()
        if (Object.keys(errs).length > 0) {
            setErrors(errs)
            return
        }

        setSaving(true)
        setSubmitError(null)

        try {
            const numericFields = ['volumeBBL', 'swapPrice', 'putStrike', 'callStrike', 'shortPutStrike', 'participationRate', 'premiumPaid', 'swaptionStrike', 'swaptionPremium']
            const body = { ...form }
            numericFields.forEach(f => {
                if (body[f] !== '' && body[f] != null) body[f] = Number(body[f])
                else delete body[f]
            })

            if (isEdit) {
                body.positionId = editPosition.PositionId
                const res = await fetch(`${apiConfig.baseUrl}/hedge/positions`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                })
                if (!res.ok) {
                    const json = await res.json().catch(() => ({}))
                    throw new Error(json.error || `HTTP ${res.status}`)
                }
            } else {
                if (bulkMode) {
                    const contractMonths = buildMonthRange(bulkStart, bulkEnd)
                    if (contractMonths.length === 0) {
                        setErrors(prev => ({ ...prev, bulkStart: true, bulkEnd: true }))
                        setSaving(false)
                        return
                    }
                    body.contractMonths = contractMonths
                    delete body.contractMonth
                } else {
                    body.contractMonth = toFirstOfMonth(form.contractMonth)
                }

                const res = await fetch(`${apiConfig.baseUrl}/hedge/positions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                })
                if (!res.ok) {
                    const json = await res.json().catch(() => ({}))
                    throw new Error(json.error || `HTTP ${res.status}`)
                }
            }

            onSaved()
        } catch (err) {
            setSubmitError(err.message)
        } finally {
            setSaving(false)
        }
    }

    const inputBase = `w-full rounded-lg border px-3 py-2 text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        isDark
            ? 'bg-slate-800 border-slate-600 text-gray-200 placeholder-gray-500'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
    }`
    const inputError = `ring-2 ring-red-500 border-red-500`
    const labelClass = `block text-xs font-semibold mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`
    const sectionClass = `grid grid-cols-2 gap-3`

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal panel */}
            <div className={`relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
                isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'
            }`}>
                {/* Modal header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {isEdit ? 'Edit Position' : 'Add Hedge Position'}
                    </h2>
                    <button
                        onClick={onClose}
                        className={`p-1.5 rounded-lg transition ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-slate-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                        aria-label="Close modal"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5" noValidate>
                    {/* Structure type */}
                    <div>
                        <label className={labelClass}>Structure Type *</label>
                        <select
                            value={form.structureType}
                            onChange={e => setField('structureType', e.target.value)}
                            className={inputBase}
                        >
                            {STRUCTURE_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Bulk toggle (create only) */}
                    {!isEdit && (
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={bulkMode}
                                onClick={() => setBulkMode(v => !v)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                                    bulkMode ? 'bg-blue-600' : (isDark ? 'bg-slate-600' : 'bg-gray-300')
                                }`}
                            >
                                <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform ${bulkMode ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                Date range (create same position across a month range, e.g. Jul–Dec)
                            </span>
                        </div>
                    )}

                    {/* Contract month(s) */}
                    {!isEdit && bulkMode ? (
                        <div className={sectionClass}>
                            <div>
                                <label className={labelClass}>Start Month *</label>
                                <input
                                    type="month"
                                    value={bulkStart ? bulkStart.substring(0, 7) : ''}
                                    onChange={e => {
                                        setBulkStart(e.target.value ? e.target.value + '-01' : '')
                                        if (errors.bulkStart) setErrors(prev => ({ ...prev, bulkStart: false }))
                                    }}
                                    className={`${inputBase} ${errors.bulkStart ? inputError : ''}`}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>End Month *</label>
                                <input
                                    type="month"
                                    value={bulkEnd ? bulkEnd.substring(0, 7) : ''}
                                    onChange={e => {
                                        setBulkEnd(e.target.value ? e.target.value + '-01' : '')
                                        if (errors.bulkEnd) setErrors(prev => ({ ...prev, bulkEnd: false }))
                                    }}
                                    className={`${inputBase} ${errors.bulkEnd ? inputError : ''}`}
                                />
                            </div>
                            {bulkStart && bulkEnd && (
                                <div className="col-span-2">
                                    <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                        {buildMonthRange(bulkStart, bulkEnd).length} month(s) will be created
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <label className={labelClass}>Contract Month *</label>
                            <input
                                type="month"
                                value={form.contractMonth ? form.contractMonth.substring(0, 7) : ''}
                                onChange={e => setField('contractMonth', e.target.value ? e.target.value + '-01' : '')}
                                className={`${inputBase} ${errors.contractMonth ? inputError : ''}`}
                                aria-invalid={!!errors.contractMonth}
                            />
                        </div>
                    )}

                    {/* Volume */}
                    <div>
                        <label className={labelClass}>Volume (BBL) *</label>
                        <input
                            type="number"
                            min="1"
                            step="1"
                            value={form.volumeBBL}
                            onChange={e => setField('volumeBBL', e.target.value)}
                            placeholder="e.g. 15000"
                            className={`${inputBase} ${errors.volumeBBL ? inputError : ''}`}
                            aria-invalid={!!errors.volumeBBL}
                        />
                    </div>

                    {/* SWAP fields */}
                    {type === 'SWAP' && (
                        <div>
                            <label className={labelClass}>Swap Price ($/BBL) *</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.swapPrice}
                                onChange={e => setField('swapPrice', e.target.value)}
                                placeholder="e.g. 72.00"
                                className={`${inputBase} ${errors.swapPrice ? inputError : ''}`}
                                aria-invalid={!!errors.swapPrice}
                            />
                        </div>
                    )}

                    {/* PUT fields */}
                    {type === 'PUT' && (
                        <div className={sectionClass}>
                            <div>
                                <label className={labelClass}>Put Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.putStrike}
                                    onChange={e => setField('putStrike', e.target.value)}
                                    placeholder="e.g. 65.00"
                                    className={`${inputBase} ${errors.putStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Premium Paid ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.premiumPaid}
                                    onChange={e => setField('premiumPaid', e.target.value)}
                                    placeholder="e.g. 2.50"
                                    className={`${inputBase} ${errors.premiumPaid ? inputError : ''}`} />
                            </div>
                        </div>
                    )}

                    {/* COLLAR fields */}
                    {type === 'COLLAR' && (
                        <div className={sectionClass}>
                            <div>
                                <label className={labelClass}>Put Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.putStrike}
                                    onChange={e => setField('putStrike', e.target.value)}
                                    placeholder="e.g. 65.00"
                                    className={`${inputBase} ${errors.putStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Call Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.callStrike}
                                    onChange={e => setField('callStrike', e.target.value)}
                                    placeholder="e.g. 85.00"
                                    className={`${inputBase} ${errors.callStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Premium Paid ($/BBL)</label>
                                <input type="number" step="0.01" value={form.premiumPaid}
                                    onChange={e => setField('premiumPaid', e.target.value)}
                                    placeholder="e.g. 0.00"
                                    className={inputBase} />
                            </div>
                        </div>
                    )}

                    {/* THREE_WAY_COLLAR fields */}
                    {type === 'THREE_WAY_COLLAR' && (
                        <div className={sectionClass}>
                            <div>
                                <label className={labelClass}>Short Put Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.shortPutStrike}
                                    onChange={e => setField('shortPutStrike', e.target.value)}
                                    placeholder="e.g. 55.00"
                                    className={`${inputBase} ${errors.shortPutStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Put Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.putStrike}
                                    onChange={e => setField('putStrike', e.target.value)}
                                    placeholder="e.g. 65.00"
                                    className={`${inputBase} ${errors.putStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Call Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.callStrike}
                                    onChange={e => setField('callStrike', e.target.value)}
                                    placeholder="e.g. 85.00"
                                    className={`${inputBase} ${errors.callStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Premium Paid ($/BBL)</label>
                                <input type="number" step="0.01" value={form.premiumPaid}
                                    onChange={e => setField('premiumPaid', e.target.value)}
                                    placeholder="e.g. 0.00"
                                    className={inputBase} />
                            </div>
                        </div>
                    )}

                    {/* PARTICIPATING_COLLAR fields */}
                    {type === 'PARTICIPATING_COLLAR' && (
                        <div className={sectionClass}>
                            <div>
                                <label className={labelClass}>Put Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.putStrike}
                                    onChange={e => setField('putStrike', e.target.value)}
                                    placeholder="e.g. 65.00"
                                    className={`${inputBase} ${errors.putStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Call Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.callStrike}
                                    onChange={e => setField('callStrike', e.target.value)}
                                    placeholder="e.g. 85.00"
                                    className={`${inputBase} ${errors.callStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Participation Rate (%) *</label>
                                <input type="number" step="1" min="0" max="100" value={form.participationRate}
                                    onChange={e => setField('participationRate', e.target.value)}
                                    placeholder="e.g. 50"
                                    className={`${inputBase} ${errors.participationRate ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Premium Paid ($/BBL)</label>
                                <input type="number" step="0.01" value={form.premiumPaid}
                                    onChange={e => setField('premiumPaid', e.target.value)}
                                    placeholder="e.g. 0.00"
                                    className={inputBase} />
                            </div>
                        </div>
                    )}

                    {/* SWAPTION fields */}
                    {type === 'SWAPTION' && (
                        <div className={sectionClass}>
                            <div>
                                <label className={labelClass}>Swaption Strike ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.swaptionStrike}
                                    onChange={e => setField('swaptionStrike', e.target.value)}
                                    placeholder="e.g. 70.00"
                                    className={`${inputBase} ${errors.swaptionStrike ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Swaption Expiry *</label>
                                <input type="date" value={form.swaptionExpiry}
                                    onChange={e => setField('swaptionExpiry', e.target.value)}
                                    className={`${inputBase} ${errors.swaptionExpiry ? inputError : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Swaption Premium ($/BBL) *</label>
                                <input type="number" step="0.01" value={form.swaptionPremium}
                                    onChange={e => setField('swaptionPremium', e.target.value)}
                                    placeholder="e.g. 1.25"
                                    className={`${inputBase} ${errors.swaptionPremium ? inputError : ''}`} />
                            </div>
                        </div>
                    )}

                    {/* Counterparty + Notes */}
                    <div className={sectionClass}>
                        <div>
                            <label className={labelClass}>Counterparty</label>
                            <input
                                type="text"
                                value={form.counterparty}
                                onChange={e => setField('counterparty', e.target.value)}
                                placeholder="e.g. BP Energy"
                                className={inputBase}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Notes</label>
                            <input
                                type="text"
                                value={form.notes}
                                onChange={e => setField('notes', e.target.value)}
                                placeholder="Optional notes"
                                className={inputBase}
                            />
                        </div>
                    </div>

                    {/* Validation error summary */}
                    {Object.keys(errors).length > 0 && (
                        <p className="text-sm text-red-400">
                            Please fill in all required fields correctly.
                        </p>
                    )}

                    {submitError && (
                        <p className={`text-sm rounded-lg px-3 py-2 ${isDark ? 'bg-red-900/30 text-red-400 border border-red-700' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            Error: {submitError}
                        </p>
                    )}

                    {/* Actions */}
                    <div className={`flex justify-end gap-3 pt-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                                isDark
                                    ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-5 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            {saving
                                ? (isEdit ? 'Saving...' : 'Creating...')
                                : (isEdit ? 'Save Changes' : (bulkMode ? 'Create Positions' : 'Create Position'))
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// ─── Deactivate Confirmation Modal ────────────────────────────────────────────

function DeactivateModal({ isDark, position, onClose, onConfirm }) {
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState(null)

    async function handleConfirm() {
        setDeleting(true)
        setError(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/hedge/positions?id=${position.PositionId}`, {
                method: 'DELETE',
            })
            if (!res.ok) {
                const json = await res.json().catch(() => ({}))
                throw new Error(json.error || `HTTP ${res.status}`)
            }
            onConfirm()
        } catch (err) {
            setError(err.message)
            setDeleting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className={`relative z-10 w-full max-w-md rounded-2xl shadow-2xl p-6 ${
                isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200'
            }`}>
                <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-red-900/40' : 'bg-red-50'}`}>
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className={`text-base font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Deactivate Position
                        </h3>
                        <p className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Deactivate {getTypeLabel(position.StructureType)} for{' '}
                            <strong>{formatContractMonth(position.ContractMonth?.substring(0, 10))}</strong>?
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            This is a soft-delete. The position will remain visible when "All" is selected.
                        </p>
                        {error && (
                            <p className="text-sm text-red-400 mt-2">Error: {error}</p>
                        )}
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button
                        onClick={onClose}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                            isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={deleting}
                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition"
                    >
                        {deleting ? 'Deactivating...' : 'Deactivate'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Main Component ───────────────────────────────────────────────────────────

function HedgePositions() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const isDark = theme === 'dark'

    const [positions, setPositions] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [showInactive, setShowInactive] = useState(false)
    const [coverageWarnings, setCoverageWarnings] = useState([])

    // Modal state
    const [showAddModal, setShowAddModal] = useState(false)
    const [editPosition, setEditPosition] = useState(null)
    const [deactivateTarget, setDeactivateTarget] = useState(null)

    // Sort state
    const [sortKey, setSortKey] = useState('ContractMonth')
    const [sortDir, setSortDir] = useState('asc')

    const fetchPositions = useCallback(async (inactive) => {
        setLoading(true)
        setError(null)
        try {
            const url = inactive
                ? `${apiConfig.baseUrl}/hedge/positions?inactive=1`
                : `${apiConfig.baseUrl}/hedge/positions`
            const res = await fetch(url)
            if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to load positions`)
            const json = await res.json()
            setPositions(json.positions || [])
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchCoverageWarnings = useCallback(async () => {
        try {
            const res = await fetch(`${apiConfig.baseUrl}/hedge`)
            if (!res.ok) return
            const json = await res.json()

            // Check months where hedged volume > forecast volume
            const months = json.months || []
            const warnings = months.filter(m => {
                const hedgedVol = Number(m.hedgedVolume) || 0
                const forecastVol = Number(m.forecastVolume) || 0
                return forecastVol > 0 && hedgedVol > forecastVol
            })
            setCoverageWarnings(warnings)
        } catch {
            // Non-critical; silently ignore coverage check failures
        }
    }, [])

    useEffect(() => {
        fetchPositions(showInactive)
    }, [showInactive, fetchPositions])

    useEffect(() => {
        fetchCoverageWarnings()
    }, [fetchCoverageWarnings])

    function handleSort(key) {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(key)
            setSortDir('asc')
        }
    }

    const sortedPositions = [...positions].sort((a, b) => {
        let av = a[sortKey]
        let bv = b[sortKey]
        if (av == null) av = ''
        if (bv == null) bv = ''
        if (typeof av === 'string') av = av.toLowerCase()
        if (typeof bv === 'string') bv = bv.toLowerCase()
        if (av < bv) return sortDir === 'asc' ? -1 : 1
        if (av > bv) return sortDir === 'asc' ? 1 : -1
        return 0
    })

    function SortIcon({ col }) {
        if (sortKey !== col) {
            return (
                <svg className="w-3 h-3 opacity-30 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            )
        }
        return sortDir === 'asc' ? (
            <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
        ) : (
            <svg className="w-3 h-3 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        )
    }

    // Summary KPIs
    const activePositions = positions.filter(p => p.IsActive !== false && p.IsActive !== 0)
    const totalVolume = activePositions.reduce((s, p) => s + (Number(p.VolumeBBL) || 0), 0)
    const totalMTM = activePositions.reduce((s, p) => s + calcMTM(p), 0)
    const uniqueMonths = new Set(activePositions.map(p => p.ContractMonth?.substring(0, 7))).size

    const thClass = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap ${
        isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
    }`
    const tdClass = `px-3 py-2.5 text-sm whitespace-nowrap`
    const trBase = `border-t transition ${isDark ? 'border-slate-700/60 hover:bg-slate-800/50' : 'border-gray-100 hover:bg-gray-50'}`

    if (loading && positions.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        )
    }

    if (error && positions.length === 0) {
        return (
            <div className={`rounded-lg p-5 ${isDark ? 'bg-red-900/20 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                <p className="font-semibold">Failed to load hedge positions</p>
                <p className="text-sm mt-1">{error}</p>
                <button
                    onClick={() => fetchPositions(showInactive)}
                    className="mt-3 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg transition"
                >
                    Retry
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        {/* Back link */}
                        <button
                            onClick={() => navigate('/hedge')}
                            className={`flex items-center gap-1 text-xs mb-2 transition ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                            aria-label="Back to hedge overview"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Hedge Overview
                        </button>
                        <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                            <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                            Hedge Positions
                        </h1>
                        <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                            Manage oil hedge positions &bull; {activePositions.length} active{loading && <span className="ml-2 opacity-60">Refreshing...</span>}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap ml-3 sm:ml-0">
                        {/* Active / All toggle */}
                        <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-gray-200 border border-gray-300'}`}>
                            <button
                                onClick={() => setShowInactive(false)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                    !showInactive
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Active
                            </button>
                            <button
                                onClick={() => setShowInactive(true)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                                    showInactive
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                All
                            </button>
                        </div>

                        <button
                            onClick={() => fetchPositions(showInactive)}
                            title="Refresh"
                            className={`p-2 rounded-lg transition ${
                                isDark ? 'bg-slate-800 border border-slate-700 text-gray-400 hover:text-gray-200' : 'bg-white border border-gray-300 text-gray-500 hover:text-gray-700'
                            }`}
                            aria-label="Refresh positions"
                        >
                            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>

                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition shadow-sm"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Position
                        </button>
                    </div>
                </div>
            </div>

            {/* Coverage Warning Banner */}
            {coverageWarnings.length > 0 && (
                <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
                    isDark ? 'bg-yellow-900/20 border-yellow-700/50 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                }`} role="alert">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <div>
                        <p className="font-semibold text-sm">Over-hedged months detected</p>
                        <p className="text-xs mt-0.5">
                            {coverageWarnings.length} month{coverageWarnings.length > 1 ? 's' : ''} exceed 100% of forecast production:{' '}
                            {coverageWarnings.map(w => formatContractMonth(w.month)).join(', ')}
                        </p>
                    </div>
                </div>
            )}

            {/* Summary KPI strip */}
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3`}>
                {[
                    { label: 'Active Positions', value: String(activePositions.length) },
                    { label: 'Hedged Months', value: String(uniqueMonths) },
                    {
                        label: 'Total Volume',
                        value: totalVolume > 0 ? `${(totalVolume / 1000).toFixed(0)}K BBL` : '0 BBL'
                    },
                    {
                        label: 'Total MTM',
                        value: fmtMTM(totalMTM),
                        valueColor: totalMTM >= 0
                            ? (isDark ? 'text-green-400' : 'text-green-600')
                            : (isDark ? 'text-red-400' : 'text-red-600'),
                    },
                ].map(kpi => (
                    <div key={kpi.label} className={`rounded-xl px-4 py-3 ${isDark ? 'bg-slate-800/60 border border-slate-700' : 'bg-white border border-gray-200 shadow-sm'}`}>
                        <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{kpi.label}</p>
                        <p className={`text-xl font-bold mt-0.5 ${kpi.valueColor || (isDark ? 'text-white' : 'text-gray-900')}`}>{kpi.value}</p>
                    </div>
                ))}
            </div>

            {/* Positions Table */}
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                {sortedPositions.length === 0 ? (
                    <div className={`px-6 py-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <svg className="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm font-medium">No positions found</p>
                        <p className="text-xs mt-1">Add your first hedge position using the button above.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className={`w-full text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            <thead className={isDark ? 'bg-slate-800/80' : 'bg-gray-50'}>
                                <tr>
                                    <th className={thClass} onClick={() => handleSort('ContractMonth')}>
                                        Month <SortIcon col="ContractMonth" />
                                    </th>
                                    <th className={thClass} onClick={() => handleSort('StructureType')}>
                                        Type <SortIcon col="StructureType" />
                                    </th>
                                    <th className={`${thClass} text-right`} onClick={() => handleSort('VolumeBBL')}>
                                        Volume (BBL) <SortIcon col="VolumeBBL" />
                                    </th>
                                    <th className={thClass}>Strike / Price</th>
                                    <th className={`${thClass} text-right`} onClick={() => handleSort('PremiumPaid')}>
                                        Premium <SortIcon col="PremiumPaid" />
                                    </th>
                                    <th className={`${thClass} text-right`} onClick={() => handleSort('StripPrice')}>
                                        Strip <SortIcon col="StripPrice" />
                                    </th>
                                    <th className={`${thClass} text-right`}>MTM</th>
                                    <th className={thClass} onClick={() => handleSort('Counterparty')}>
                                        Counterparty <SortIcon col="Counterparty" />
                                    </th>
                                    <th className={thClass}>Status</th>
                                    <th className={`${thClass} text-right`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedPositions.map(pos => {
                                    const isInactive = pos.IsActive === false || pos.IsActive === 0
                                    const mtm = isInactive ? null : calcMTM(pos)
                                    const rowClass = isInactive
                                        ? `${trBase} opacity-50`
                                        : trBase

                                    return (
                                        <tr key={pos.PositionId} className={rowClass}>
                                            {/* Month */}
                                            <td className={`${tdClass} font-medium ${isInactive ? 'line-through' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {formatContractMonth(pos.ContractMonth?.substring(0, 10))}
                                            </td>

                                            {/* Type badge */}
                                            <td className={tdClass}>
                                                <TypeBadge type={pos.StructureType} isDark={isDark} />
                                            </td>

                                            {/* Volume */}
                                            <td className={`${tdClass} text-right font-mono`}>
                                                {Number(pos.VolumeBBL)?.toLocaleString() || '--'}
                                            </td>

                                            {/* Strike / Price */}
                                            <td className={`${tdClass} font-mono text-xs`}>
                                                {getStrikeDisplay(pos)}
                                            </td>

                                            {/* Premium */}
                                            <td className={`${tdClass} text-right font-mono`}>
                                                {pos.PremiumPaid != null ? fmtPrice(pos.PremiumPaid) : '--'}
                                            </td>

                                            {/* Strip price */}
                                            <td className={`${tdClass} text-right font-mono`}>
                                                {pos.StripPrice != null ? fmtPrice(pos.StripPrice) : '--'}
                                            </td>

                                            {/* MTM */}
                                            <td className={`${tdClass} text-right`}>
                                                <MTMCell mtm={mtm} isDark={isDark} />
                                            </td>

                                            {/* Counterparty */}
                                            <td className={`${tdClass} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {pos.Counterparty || '--'}
                                            </td>

                                            {/* Status */}
                                            <td className={tdClass}>
                                                {isInactive ? (
                                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                                        isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                        Inactive
                                                    </span>
                                                ) : (
                                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                                        isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700'
                                                    }`}>
                                                        Active
                                                    </span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td className={`${tdClass} text-right`}>
                                                <div className="flex items-center justify-end gap-1">
                                                    {/* Edit */}
                                                    <button
                                                        onClick={() => setEditPosition(pos)}
                                                        title="Edit position"
                                                        aria-label={`Edit ${getTypeLabel(pos.StructureType)} position for ${formatContractMonth(pos.ContractMonth?.substring(0, 10))}`}
                                                        className={`p-1.5 rounded-lg transition ${
                                                            isDark
                                                                ? 'text-gray-400 hover:text-blue-400 hover:bg-slate-700'
                                                                : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                                                        }`}
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>

                                                    {/* Deactivate (only for active positions) */}
                                                    {!isInactive && (
                                                        <button
                                                            onClick={() => setDeactivateTarget(pos)}
                                                            title="Deactivate position"
                                                            aria-label={`Deactivate ${getTypeLabel(pos.StructureType)} position for ${formatContractMonth(pos.ContractMonth?.substring(0, 10))}`}
                                                            className={`p-1.5 rounded-lg transition ${
                                                                isDark
                                                                    ? 'text-gray-400 hover:text-red-400 hover:bg-slate-700'
                                                                    : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                                                            }`}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Footer note */}
            <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                MTM (mark-to-market) is computed client-side using current strip prices.
                Positive MTM = in-the-money. Strip prices provided by the API.
            </p>

            {/* Add / Edit Modal */}
            {(showAddModal || editPosition) && (
                <PositionModal
                    isDark={isDark}
                    editPosition={editPosition || null}
                    onClose={() => {
                        setShowAddModal(false)
                        setEditPosition(null)
                    }}
                    onSaved={() => {
                        setShowAddModal(false)
                        setEditPosition(null)
                        fetchPositions(showInactive)
                        fetchCoverageWarnings()
                    }}
                />
            )}

            {/* Deactivate Confirmation Modal */}
            {deactivateTarget && (
                <DeactivateModal
                    isDark={isDark}
                    position={deactivateTarget}
                    onClose={() => setDeactivateTarget(null)}
                    onConfirm={() => {
                        setDeactivateTarget(null)
                        fetchPositions(showInactive)
                        fetchCoverageWarnings()
                    }}
                />
            )}
        </div>
    )
}

export default HedgePositions
